// Part detail page — M2 Phase 5.3+. Route: /parts/:id
//
// Two visual modes, gated by `editing`:
//   - Read mode: header card + tabs (Overview / History / Used in quotes)
//   - Edit mode: embedded PartWizard (replaces the tab area entirely)
//
// Read mode mirrors CustomerDetailPage's layout pattern — back link →
// header card → tab strip → tab-specific cards. Overview groups fields
// into per-card sections, rendered conditionally on part.type so a
// "purchased" part only shows source + markup, a "weldment" shows
// sub-parts + weld ops, etc.
//
// "Used in quotes" tab lazily loads all quotes on first activation (the
// quotes list is small enough that a single listQuotes + client-side
// filter is cheaper than wiring a new index). Result is cached on this
// instance so flipping between tabs doesn't refetch.
//
// Delete + quote row click are stubbed (alert / navigate to a route that
// doesn't exist yet) — both land in later passes. Pre-wiring the
// navigate keeps the call shape consistent with QuotesPage.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPart, listQuotes, updatePart } from '@/shared/crm';
import type {
  AuditEntry,
  DxfFileEntry,
  MachineSetup,
  OutsideService,
  Part,
  PartType,
  PurchasedSource,
  Quote,
  ToolingEntry,
  WeldmentSpec,
} from '@/shared/crmTypes';
import { PartTypeBadge, PART_TYPE_META } from '@/components/PartTypeBadge';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import { PartWizard } from '@/components/PartWizard/PartWizard';
import { PartGraphic } from '@/components/PartGraphic';
import { PartDrawingThumbnails } from '@/components/PartDrawingThumbnails';

type Tab = 'overview' | 'graphics' | 'history' | 'usedIn';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'graphics', label: 'Graphics' },
  { key: 'history', label: 'History' },
  { key: 'usedIn', label: 'Used in quotes' },
];

export function PartDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [part, setPart] = useState<Part | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  // Wizard draft — initialized from the loaded part and reset whenever
  // the canonical part record refreshes (load() or post-save reload).
  const [draft, setDraft] = useState<(Partial<Part> & { type: PartType }) | null>(null);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const p = await getPart(id);
      setPart(p);
      // Seed the wizard draft from the canonical record so edit mode
      // starts with the latest persisted values.
      if (p) setDraft(p);
    } catch (e) {
      setError(`Failed to load part: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
          Loading part…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={load}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/parts')}
          className="text-sm text-orange hover:underline"
        >
          ← Back to parts
        </button>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">🔍</div>
          <div className="font-semibold text-navy mb-1">Part not found</div>
          <p className="text-sm text-text-mute mb-4">
            ID <span className="font-mono">{id}</span> doesn't exist or was soft-deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate('/parts')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            ← Back to parts
          </button>
        </div>
      </div>
    );
  }

  function handleDelete() {
    // Soft-delete UI lands in a later pass — confirm dialog, audit-trail
    // entry, undo affordance, etc. Stub keeps the button live in the
    // header so the design reads correctly.
    alert('TODO: soft-delete part (M2 Phase 5.3.x)');
  }

  function handleEnterEdit() {
    // Reset draft to the latest canonical record before opening the
    // wizard — protects against stale draft from a previous edit/cancel
    // cycle on the same page instance.
    if (part) setDraft(part);
    setEditing(true);
  }

  function handleCancelEdit() {
    setEditing(false);
    // Drop the in-progress draft — load() will reseed on the next edit.
    if (part) setDraft(part);
  }

  async function handleSave(p: Partial<Part> & { type: PartType }) {
    if (!part) return;
    // Strip baseline + id keys before forwarding — crm.updatePart accepts
    // only mutable fields and would otherwise fight withUpdateStamp's
    // auto-bump of updatedAt. Built as a plain projection (vs. rest
    // destructure) to keep noUnusedLocals happy.
    const baselineKeys = new Set([
      'id',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
      '_v',
      '_audit',
      '_deleted',
      '_deletedAt',
      '_deletedBy',
      '_deletedReason',
    ]);
    const mutable: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(p)) {
      if (!baselineKeys.has(k)) mutable[k] = v;
    }
    await updatePart(part.id, mutable as Parameters<typeof updatePart>[1]);
    await load();
    setEditing(false);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/parts')}
        className="text-xs text-text-mute hover:text-orange mb-2"
      >
        ← All parts
      </button>

      <PartHeader
        part={part}
        editing={editing}
        onEdit={handleEnterEdit}
        onDelete={handleDelete}
      />

      {editing && draft && (
        <div className="mt-4">
          <PartWizard
            embedded
            part={draft}
            onChange={(updates) =>
              // Merge wizard-driven updates into the draft. Re-assert the
              // `type` field after the spread so TS keeps the narrowed
              // `{ type: PartType }` shape — type changes mid-edit aren't
              // a supported flow (would need a fresh wizard mount).
              setDraft((prev) =>
                prev ? { ...prev, ...updates, type: prev.type } : prev,
              )
            }
            onSave={handleSave}
            onCancel={handleCancelEdit}
          />
        </div>
      )}

      {!editing && (
        <>
          {/* Tabs */}
          <div className="border-b border-line mt-4 mb-4 flex flex-wrap">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                    active
                      ? 'border-orange text-orange'
                      : 'border-transparent text-text-mute hover:text-navy'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === 'overview' && <OverviewTab part={part} />}
          {tab === 'graphics' && <GraphicsTab part={part} onChanged={load} />}
          {tab === 'history' && <HistoryTab part={part} />}
          {tab === 'usedIn' && <UsedInQuotesTab part={part} />}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────

interface PartHeaderProps {
  part: Part;
  editing: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function PartHeader({ part, editing, onEdit, onDelete }: PartHeaderProps) {
  return (
    <div className="bg-paper border border-line rounded p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1
              className="text-2xl font-bold font-mono text-navy break-all"
              title={part.number}
            >
              {part.number}
            </h1>
            <PartTypeBadge type={part.type} size="md" />
          </div>
          <p className="text-sm text-text mt-1">{part.description}</p>
        </div>

        {/* Hide actions while editing — the wizard owns its own action row */}
        {!editing && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg hover:text-orange"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overview tab — per-type card grid
// ─────────────────────────────────────────────────────────────

function OverviewTab({ part }: { part: Part }) {
  // Decide which cards render off the discriminator. Some cards (Material,
  // Setups) appear for both `machined` and `hybrid`; Sheet+DXF appears for
  // both `cut_blank` and `hybrid`; Sub-parts/Weld-ops are weldment-only;
  // Source/Markup are purchased-only. Outside services / tooling / finishing
  // notes hang off every type — render only when content exists.
  const t = part.type;
  const showMaterial = t === 'machined' || t === 'hybrid';
  const showSetups = t === 'machined' || t === 'hybrid';
  const showSheet = t === 'cut_blank' || t === 'hybrid';
  const showWeldment = t === 'weldment';
  const showPurchased = t === 'purchased';

  const hasOutside = (part.outsideServices?.length ?? 0) > 0;
  const hasTooling = (part.tooling?.length ?? 0) > 0;
  const hasFinishingNotes = !!part.notes && part.notes.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <IdentityCard part={part} />
        {showMaterial && <MaterialCard part={part} />}
        {showSetups && (
          <SetupsCard machines={part.machines} setupHrs={part.setupHrs} />
        )}
        {showSheet && <SheetDxfCard dxfFiles={part.dxfFiles} />}
        {showWeldment && <WeldmentCard weldment={part.weldment} />}
        {showPurchased && (
          <>
            <PurchasedSourceCard source={part.purchasedSource} />
            <MarkupCard source={part.purchasedSource} />
          </>
        )}
        {hasOutside && <OutsideServicesCard services={part.outsideServices!} />}
        {hasTooling && <ToolingCard tooling={part.tooling!} />}
        {hasFinishingNotes && <FinishingNotesCard notes={part.notes!} />}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-paper border border-line rounded p-4">
      <h3 className="font-semibold text-navy mb-3">{title}</h3>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="text-text">{value}</div>
    </div>
  );
}

function fmtCurrency(n: number | undefined | null): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function IdentityCard({ part }: { part: Part }) {
  const meta = PART_TYPE_META[part.type];
  return (
    <Card title="Identity">
      <div className="space-y-3">
        <FieldRow label="Part number" value={<span className="font-mono">{part.number}</span>} />
        <FieldRow label="Description" value={part.description} />
        <FieldRow
          label="Type"
          value={
            <span className="inline-flex items-center gap-2">
              <PartTypeBadge type={part.type} size="sm" />
              <span className="text-text-mute">{meta.label}</span>
            </span>
          }
        />
        <FieldRow
          label="Qty tiers"
          value={
            part.qtyTiers && part.qtyTiers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {part.qtyTiers.map((q, i) => (
                  <span
                    key={`${q}-${i}`}
                    className="text-[11px] bg-bg border border-line-dark rounded-full px-2 py-0.5 font-mono"
                  >
                    {q}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-text-mute italic">No tiers set</span>
            )
          }
        />
        {part.notes && part.notes.trim() && (
          <FieldRow
            label="Notes"
            value={<span className="whitespace-pre-wrap break-words">{part.notes}</span>}
          />
        )}
      </div>
    </Card>
  );
}

function MaterialCard({ part }: { part: Part }) {
  const material = part.materialCost;
  const csm = part.customerSuppliedMaterial === true;

  // CSM gets its own treatment — surface the "(customer-supplied)" label
  // prominently, show the handling fee, skip the vendor/snapshot block
  // (KMF didn't buy anything). Mirrors v1's `materialLabelFor` pattern
  // (index.html ~5513) where the suffix is shown anywhere material name
  // is rendered in the shop-facing app.
  if (csm) {
    return (
      <Card title="Material">
        <div className="space-y-3">
          <div className="bg-orange/10 border-l-4 border-orange rounded-r px-3 py-2">
            <div className="text-sm font-semibold text-text">
              {material?.vendorPartNum || 'Material'}{' '}
              <span className="text-orange italic font-normal">(customer-supplied)</span>
            </div>
            <div className="text-[11px] text-text-mute mt-0.5">
              KMF doesn't buy stock for this part — handling fee charged in lieu of material markup.
            </div>
          </div>
          <FieldRow
            label="Handling fee per piece"
            value={
              <span className="font-mono font-semibold">
                {fmtCurrency(part.csmHandlingFeePerPiece)}
              </span>
            }
          />
        </div>
      </Card>
    );
  }

  if (!material) {
    return (
      <Card title="Material">
        <div className="text-text-mute italic">No material spec yet.</div>
      </Card>
    );
  }
  const perPiece = material.perPiece ?? [];
  return (
    <Card title="Material">
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Per-piece cost (by tier)
          </div>
          {perPiece.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {perPiece.map((cost, i) => (
                <span
                  key={`${cost}-${i}`}
                  className="text-xs bg-bg border border-line-dark rounded px-2 py-0.5 font-mono"
                  title={`Tier ${i + 1}`}
                >
                  T{i + 1}: {fmtCurrency(cost)}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-text-mute italic">No per-piece pricing entered.</div>
          )}
        </div>
        {(material.vendorId || material.vendorPartNum || material.vendorUrl) && (
          <div className="border-t border-line pt-2">
            <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
              Vendor
            </div>
            <div className="space-y-0.5 text-xs">
              {material.vendorId && (
                <div>
                  <span className="text-text-mute">ID: </span>
                  <span className="font-mono">{material.vendorId}</span>
                </div>
              )}
              {material.vendorPartNum && (
                <div>
                  <span className="text-text-mute">Part #: </span>
                  <span className="font-mono">{material.vendorPartNum}</span>
                </div>
              )}
              {material.vendorUrl && (
                <div>
                  <a
                    href={material.vendorUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange hover:underline break-all"
                  >
                    {material.vendorUrl}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
        {material.snapshotAt && (
          <div className="text-[11px] text-text-mute italic">
            Price snapshot {new Date(material.snapshotAt).toLocaleDateString()}
            {material.snapshotSource && ` · ${material.snapshotSource.replace(/_/g, ' ')}`}
          </div>
        )}
      </div>
    </Card>
  );
}

function SetupsCard({
  machines,
  setupHrs,
}: {
  machines?: MachineSetup[];
  setupHrs?: number;
}) {
  return (
    <Card title="Setups">
      <div className="space-y-3">
        {setupHrs !== undefined && (
          <FieldRow
            label="One-time setup hrs"
            value={<span className="font-mono">{setupHrs.toFixed(2)} hr</span>}
          />
        )}
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Machines
          </div>
          {machines && machines.length > 0 ? (
            <ul className="space-y-1.5">
              {machines.map((m, i) => (
                <li
                  key={`${m.machine}-${i}`}
                  className="border-l-2 border-line pl-2 text-xs"
                >
                  <div className="font-semibold text-text">{m.machine}</div>
                  <div className="text-text-mute font-mono">
                    setup {m.setupHrs.toFixed(2)} hr · runtime{' '}
                    {m.hrsPerTier?.length ? m.hrsPerTier.map((h) => h.toFixed(2)).join(' / ') : '—'}{' '}
                    hr/tier
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-text-mute italic">No machines assigned.</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function SheetDxfCard({ dxfFiles }: { dxfFiles?: DxfFileEntry[] }) {
  return (
    <Card title="Sheet stock + DXF">
      {dxfFiles && dxfFiles.length > 0 ? (
        <ul className="space-y-2">
          {dxfFiles.map((d, i) => (
            <li key={`${d.filename}-${i}`} className="border border-line rounded p-2 text-xs">
              <div className="font-mono font-semibold text-text break-all">{d.filename}</div>
              <div className="text-text-mute mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {d.vendor && <span>vendor: {d.vendor}</span>}
                {d.setupCost !== undefined && (
                  <span className="font-mono">setup: {fmtCurrency(d.setupCost)}</span>
                )}
                {d.perPieceCost !== undefined && (
                  <span className="font-mono">per-pc: {fmtCurrency(d.perPieceCost)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-text-mute italic">No DXF files uploaded yet.</div>
      )}
    </Card>
  );
}

function WeldmentCard({ weldment }: { weldment?: WeldmentSpec }) {
  return (
    <Card title="Sub-parts + weld ops">
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Sub-parts ({weldment?.subParts?.length ?? 0})
          </div>
          {weldment?.subParts && weldment.subParts.length > 0 ? (
            <ul className="space-y-1">
              {weldment.subParts.map((sp, i) => (
                // sp.id is required per the schema, but historic / migrated
                // weldments may not carry one — fall back to number+index.
                <li
                  key={sp.id || `${sp.number}-${i}`}
                  className="text-xs flex items-center gap-2"
                >
                  <span className="font-mono font-semibold text-text">{sp.number}</span>
                  <span className="text-text-mute truncate">{sp.description}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-text-mute italic">No sub-parts.</div>
          )}
        </div>
        <div className="border-t border-line pt-2">
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Weld ops
          </div>
          {weldment?.weldOps && weldment.weldOps.length > 0 ? (
            <ul className="space-y-1">
              {weldment.weldOps.map((op, i) => (
                <li
                  key={`${op.method}-${i}`}
                  className="text-xs flex items-center gap-2"
                >
                  <span className="font-semibold text-text">{op.method}</span>
                  <span className="text-text-mute font-mono">{op.hrs.toFixed(2)} hr</span>
                  {op.electrodeType && (
                    <span className="text-text-mute">· {op.electrodeType}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-text-mute italic">No weld ops.</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function PurchasedSourceCard({ source }: { source?: PurchasedSource }) {
  if (!source) {
    return (
      <Card title="Source">
        <div className="text-text-mute italic">No source set.</div>
      </Card>
    );
  }
  return (
    <Card title="Source">
      <div className="space-y-2">
        <FieldRow
          label="Vendor"
          value={<span className="font-semibold capitalize">{source.vendor}</span>}
        />
        {source.sku && (
          <FieldRow label="SKU" value={<span className="font-mono">{source.sku}</span>} />
        )}
        {source.url && (
          <FieldRow
            label="URL"
            value={
              <a
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="text-orange hover:underline break-all"
              >
                {source.url}
              </a>
            }
          />
        )}
        {source.leadTime && <FieldRow label="Lead time" value={source.leadTime} />}
      </div>
    </Card>
  );
}

function MarkupCard({ source }: { source?: PurchasedSource }) {
  return (
    <Card title="Markup">
      {source?.markupPct !== undefined ? (
        <FieldRow
          label="Markup %"
          value={<span className="font-mono font-semibold">{source.markupPct.toFixed(2)}%</span>}
        />
      ) : (
        <div className="text-text-mute italic">No markup set — defaults apply at quote time.</div>
      )}
    </Card>
  );
}

function OutsideServicesCard({ services }: { services: OutsideService[] }) {
  return (
    <Card title="Outside services">
      <ul className="space-y-2">
        {services.map((s, i) => (
          <li key={`${s.type}-${i}`} className="border border-line rounded p-2 text-xs">
            <div className="font-semibold text-text">{s.type}</div>
            <div className="text-text-mute mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {s.vendor && <span>vendor: {s.vendor}</span>}
              {s.setupCost !== undefined && (
                <span className="font-mono">setup: {fmtCurrency(s.setupCost)}</span>
              )}
              {s.perPieceCost !== undefined && (
                <span className="font-mono">per-pc: {fmtCurrency(s.perPieceCost)}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ToolingCard({ tooling }: { tooling: ToolingEntry[] }) {
  // ToolingEntry is freeform until Module 3 ships its tool entity, so we
  // dump entries as compact key/value chips rather than guessing at shape.
  return (
    <Card title="Tooling">
      <ul className="space-y-2">
        {tooling.map((entry, i) => {
          const keys = Object.keys(entry);
          return (
            <li key={i} className="border border-line rounded p-2 text-xs">
              {keys.length === 0 ? (
                <span className="text-text-mute italic">empty entry</span>
              ) : (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                  {keys.map((k) => (
                    <span key={k}>
                      <span className="text-text-mute">{k}: </span>
                      <span className="font-mono">{String(entry[k] ?? '')}</span>
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function FinishingNotesCard({ notes }: { notes: string }) {
  // Mirrors what the Identity card shows in Notes, but called out as its
  // own card so finishing-related notes (powder coat color, anodize spec,
  // etc.) sit alongside the per-type cards instead of buried in Identity.
  return (
    <Card title="Finishing notes">
      <div className="whitespace-pre-wrap break-words text-text">{notes}</div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Graphics tab — per-type visualization + attached drawings
// ─────────────────────────────────────────────────────────────

function GraphicsTab({ part, onChanged }: { part: Part; onChanged?: () => void }) {
  return (
    <div className="space-y-4">
      <div className="bg-paper border border-line rounded p-4">
        <h3 className="font-semibold text-navy mb-3 text-sm">Diagram</h3>
        <div className="flex justify-center">
          <PartGraphic part={part} width={520} />
        </div>
      </div>
      <PartDrawingThumbnails part={part} onChanged={onChanged} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// History tab — audit timeline
// ─────────────────────────────────────────────────────────────

function HistoryTab({ part }: { part: Part }) {
  // BaselineFields exposes _audit (underscore-prefixed per spec/20). The
  // data layer doesn't populate entries yet — see crm.ts TODO at the top.
  // Render an empty-state stub until that lands.
  const entries: AuditEntry[] = part._audit ?? [];

  if (entries.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">No edit history yet.</div>
        <div className="text-[11px] mt-1 italic">
          Audit entries populate once the auth/role flow lands (see crm.ts).
        </div>
      </div>
    );
  }

  // Newest first.
  const sorted = [...entries].sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
  );

  return (
    <div className="bg-paper border border-line rounded p-4">
      <ol className="space-y-3">
        {sorted.map((e, i) => (
          <li key={`${e.ts}-${i}`} className="flex gap-3">
            <div className="shrink-0 w-2 h-2 rounded-full bg-orange mt-1.5" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-text">
                <span className="font-semibold capitalize">{e.action.replace(/_/g, ' ')}</span>
                {e.fields && Object.keys(e.fields).length > 0 && (
                  <span className="text-text-mute">
                    {' '}
                    · {Object.keys(e.fields).join(', ')}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-text-mute mt-0.5 flex flex-wrap items-center gap-2">
                <time dateTime={e.ts} className="font-mono">
                  {new Date(e.ts).toLocaleString()}
                </time>
                <span className="opacity-60">·</span>
                <span>{e.user}</span>
                <span className="opacity-60">·</span>
                <span className="italic">{e.source}</span>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Used-in-quotes tab — lazy load + cache
// ─────────────────────────────────────────────────────────────

function UsedInQuotesTab({ part }: { part: Part }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // listQuotes returns the full list (filtered server-side only when
  // status/customerId set, neither apply here). The result is cached for
  // the lifetime of this tab instance — flipping tabs won't refetch.
  async function load() {
    setLoading(true);
    setError(null);
    try {
      const all = await listQuotes({});
      setQuotes(all);
    } catch (e) {
      setError(`Failed to load quotes: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (quotes === null) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => (quotes ?? []).filter((q) => q.partIds?.includes(part.id)),
    [quotes, part.id],
  );

  if (loading) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute animate-pulse">
        Loading quotes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
        <span>⚠ {error}</span>
        <button
          type="button"
          onClick={load}
          className="text-xs text-orange font-semibold hover:underline shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">This part isn't on any quotes yet.</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {filtered.map((q) => {
        const total = q.imported && q.importedTotal !== undefined
          ? q.importedTotal
          : q.totalsByTier?.[0];
        return (
          <button
            key={q.id}
            type="button"
            onClick={() => navigate(`/quotes/${q.id}`)}
            className="w-full text-left bg-paper border border-line rounded p-3 hover:border-orange hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-navy">{q.number}</span>
                  <QuoteStatusBadge status={q.status} size="sm" />
                </div>
                <div className="text-xs text-text-mute mt-0.5 truncate">
                  {q.customerNameSnapshot || <em>— no customer snapshot —</em>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-semibold text-text">
                  {total === undefined ? '—' : fmtCurrency(total)}
                </div>
                <div className="text-[10px] text-text-mute uppercase tracking-wide">
                  top tier
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
